// src/components/heatmap/CongestionLegend.js
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useColorPalette } from '../../contexts/ColorPaletteContext';
import { useDashboard } from '../../contexts/DashboardContext';
import WeatherIcon from '../ui/WeatherIcon'; // 追加
import { detectLanguageFromPathname, translate } from '../../i18n';

/** テレビ画面と同じ区切り（1〜7 少ない / 8〜12 普通 / 13〜20 多い） */
const CROWD_BANDS = [
  { id: 'low', labelKey: 'legend.bandLow', from: 1, to: 7 },
  { id: 'mid', labelKey: 'legend.bandMid', from: 8, to: 12 },
  { id: 'high', labelKey: 'legend.bandHigh', from: 13, to: 20 },
];

const levelsBetween = (from, to) => Array.from({ length: to - from + 1 }, (_, i) => from + i);

const CongestionLegend = ({ showCalculationNote = false, adminCompact = false }) => {
  const { getCellColor } = useColorPalette();
  const { action: selectedAction } = useDashboard(); // 現在のactionを取得
  const location = useLocation();
  const language = detectLanguageFromPathname(location.pathname);
  const t = (key, params) => translate(language, key, params);
  const isMobile = useMediaQuery('(max-width:768px)');

  // 天気表示が必要なactionかどうかを判定
  const shouldShowWeather = !adminCompact && selectedAction && (
    selectedAction.startsWith('cal_') || 
    selectedAction.startsWith('dti_') || 
    selectedAction.startsWith('wti_')
  );
  const shouldShowTodayBadge = !adminCompact && selectedAction === 'today_details';

  const labelSize = adminCompact ? '0.8rem' : '0.95rem';
  const noteSize = adminCompact ? '0.8rem' : '0.875rem';

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: adminCompact ? 0.35 : 0.6,
      mb: adminCompact ? 0.5 : (isMobile ? 1 : 1.5),
      mt: adminCompact ? 0 : 0.5,
    }}>
      <Box>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: adminCompact ? '0.9rem' : '1.05rem',
            lineHeight: 1.25,
          }}
        >
          {t('legend.peopleAmount')}
        </Typography>
        {!adminCompact && (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.95rem',
              lineHeight: 1.35,
            }}
          >
            {t('legend.readGuideLead')}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex' }}>
        {CROWD_BANDS.map((band) => (
          <Box
            key={`label-${band.id}`}
            sx={{
              flex: band.to - band.from + 1,
              minWidth: 0,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: labelSize, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
              {t(band.labelKey)}
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: labelSize, fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
              {t('legend.bandRange', { from: band.from, to: band.to })}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: '3px' }} aria-label={t('legend.peopleAmount')}>
        {CROWD_BANDS.map((band) => {
          const levels = levelsBetween(band.from, band.to);
          return (
            <Box key={band.id} sx={{ flex: levels.length, display: 'flex', gap: '1px', minWidth: 0 }}>
              {levels.map((level) => (
                <Box
                  key={level}
                  sx={{
                    flex: '1 1 0',
                    minWidth: 0,
                    height: adminCompact ? 16 : 28,
                    backgroundColor: getCellColor(level),
                    borderRadius: '2px',
                  }}
                  title={String(level)}
                />
              ))}
            </Box>
          );
        })}
      </Box>

      {!adminCompact && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, rowGap: 0.4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ width: 14, height: 14, backgroundColor: '#e0e0e0', border: '1px solid rgba(0,0,0,0.15)', borderRadius: '2px' }} />
            <Typography sx={{ color: 'text.secondary', fontSize: noteSize, lineHeight: 1.3 }}>
              {t('legend.noData')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ px: 0.6, py: 0.15, borderRadius: '3px', backgroundColor: 'rgba(85, 60, 154, 0.92)', color: '#fff', fontSize: noteSize, fontWeight: 700, lineHeight: 1.3 }}>
              {language === 'en' ? 'Pred' : '予測'}
            </Box>
            <Typography sx={{ color: 'text.secondary', fontSize: noteSize, lineHeight: 1.3 }}>
              {language === 'en' ? 'Prediction' : '予測値'}
            </Typography>
          </Box>
          {shouldShowTodayBadge && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ px: 0.6, py: 0.15, borderRadius: '3px', backgroundColor: 'rgba(25, 118, 210, 0.92)', color: '#fff', fontSize: noteSize, fontWeight: 700, lineHeight: 1.3 }}>
                {language === 'en' ? 'Today' : '今日'}
              </Box>
              <Typography sx={{ color: 'text.secondary', fontSize: noteSize, lineHeight: 1.3 }}>
                {language === 'en' ? 'Current day' : '当日'}
              </Typography>
            </Box>
          )}
          {shouldShowWeather && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: noteSize, lineHeight: 1.3 }}>
                {t('legend.weatherInfo')}
              </Typography>
              <WeatherIcon weather="晴れ" size="small" showTemp={false} />
              <WeatherIcon weather="曇り" size="small" showTemp={false} />
              <WeatherIcon weather="雨" size="small" showTemp={false} />
              <WeatherIcon weather="雪" size="small" showTemp={false} />
            </Box>
          )}
        </Box>
      )}

      {showCalculationNote && (
        <Typography sx={{ color: 'text.secondary', fontSize: noteSize, lineHeight: 1.35 }}>
          {t('legend.calcNote')}
        </Typography>
      )}
    </Box>
  );
};

// PropTypesの追加
CongestionLegend.propTypes = {
  showCalculationNote: PropTypes.bool,
  legendType: PropTypes.oneOf(['calendar', 'heatmap', 'trend']),
  adminCompact: PropTypes.bool,
};

export default CongestionLegend;
